import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { InvoiceService } from '../../services/InvoiceService';
import EmailService from '../../services/EmailService';
import { toast } from 'react-hot-toast';
import LineItemsManager from '../../components/LineItemsManager';
import TaxCalculator from '../../components/TaxCalculator';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const EditInvoice = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();
  const currentUser = auth.currentUser;
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [sendEmail, setSendEmail] = useState(false); // eslint-disable-line no-unused-vars
  const [userAccounts, setUserAccounts] = useState([]);

  // Fetch invoice data
  useEffect(() => {
    const fetchInvoice = async () => {
      if (!currentUser) {
        toast.error('Please sign in to edit invoices');
        navigate('/');
        return;
      }

      try {
        const invoiceService = new InvoiceService(currentUser);
        const invoice = await invoiceService.getInvoice(invoiceId);
        
        // Check if user owns this invoice
        if (invoice.userId !== currentUser.uid) {
          toast.error('You do not have permission to edit this invoice');
          navigate('/');
          return;
        }

        // Check if invoice is already paid
        if (invoice.status === 'paid') {
          toast.error('Cannot edit paid invoices');
          navigate(`/preview-invoice/${invoiceId}`);
          return;
        }

        // Convert dates if needed
        if (invoice.dueDate) {
          invoice.dueDate = invoice.dueDate.toDate ? invoice.dueDate.toDate() : new Date(invoice.dueDate);
        }

        setInvoiceData(invoice);
        
        // Fetch user email accounts
        const accounts = await EmailService.getAccounts(currentUser.uid);
        setUserAccounts(accounts.accounts || []);
        
      } catch (error) {
        console.error('Error fetching invoice:', error);
        toast.error('Failed to load invoice');
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoice();
  }, [currentUser, invoiceId, navigate]);

  // Sanitize input data
  const sanitizeInput = (value, type = 'text') => {
    if (typeof value !== 'string') return value;
    
    // Basic XSS prevention
    const sanitized = value.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    
    switch (type) {
      case 'email':
        return sanitized.toLowerCase().trim();
      case 'number':
        return sanitized.replace(/[^\d.-]/g, '');
      case 'text':
      default:
        return sanitized.trim();
    }
  };

  // Handle input changes
  const handleChange = (field, value) => {
    let sanitizedValue = value;
    
    // Apply field-specific sanitization
    if (field === 'email') {
      sanitizedValue = sanitizeInput(value, 'email');
    } else if (['amount', 'rate', 'quantity', 'taxRate'].includes(field)) {
      sanitizedValue = sanitizeInput(value, 'number');
    } else if (typeof value === 'string') {
      sanitizedValue = sanitizeInput(value, 'text');
    }
    
    setInvoiceData(prev => ({
      ...prev,
      [field]: sanitizedValue
    }));
  };

  // Calculate total
  const calculateTotal = () => {
    let subtotal = 0;
    
    if (invoiceData?.lineItems?.length > 0) {
      subtotal = invoiceData.lineItems.reduce((sum, item) => {
        return sum + (parseFloat(item.amount) || 0);
      }, 0);
    } else {
      // Simple mode calculation
      const quantity = parseFloat(invoiceData?.quantity) || 1;
      const rate = parseFloat(invoiceData?.rate) || parseFloat(invoiceData?.amount) || 0;
      subtotal = quantity * rate;
    }

    const taxAmount = invoiceData?.taxType === 'percentage' 
      ? (subtotal * (parseFloat(invoiceData?.taxRate) || 0)) / 100
      : parseFloat(invoiceData?.taxRate) || 0;

    return subtotal + taxAmount;
  };

  // Validate form data
  const validateForm = () => {
    const errors = [];
    
    if (!invoiceData.clientName && !invoiceData.client) {
      errors.push('Client name is required');
    }
    
    if (!invoiceData.email) {
      errors.push('Client email is required');
    } else if (!/\S+@\S+\.\S+/.test(invoiceData.email)) {
      errors.push('Please enter a valid email address');
    }
    
    if (!invoiceData.project) {
      errors.push('Project/description is required');
    }
    
    const total = calculateTotal();
    if (total <= 0) {
      errors.push('Invoice total must be greater than $0');
    }
    
    if (errors.length > 0) {
      toast.error(errors[0]); // Show first error
      return false;
    }
    
    return true;
  };

  // Handle save
  const handleSave = async (resend = false) => {
    if (!validateForm()) {
      return;
    }
    
    setIsSaving(true);
    
    try {
      const invoiceService = new InvoiceService(currentUser);
      
      // Prepare update data
      const updateData = {
        ...invoiceData,
        amount: calculateTotal(),
        // Clear sentDate if resending to track new send
        sentDate: resend ? null : invoiceData.sentDate
      };

      // Update the invoice with audit trail
      await invoiceService.updateInvoice(invoiceId, updateData, invoiceData);

      // Send email if requested
      if (resend && userAccounts.length > 0) {
        try {
          const invoiceUrl = `https://klorah.app/preview-invoice/${invoiceId}`;
          
          // Format the email body
          const emailBody = `
            <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
              <h2 style="color: #333; margin-bottom: 20px;">Updated Invoice</h2>
              
              <p style="color: #666; line-height: 1.6; margin-bottom: 15px;">
                Hi ${invoiceData.clientName || invoiceData.client || 'there'},
              </p>
              
              <p style="color: #666; line-height: 1.6; margin-bottom: 15px;">
                Your invoice for <strong>${invoiceData.project || 'Invoice'}</strong> has been updated.
                The updated amount is <strong>${new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: invoiceData.currency || 'USD'
                }).format(calculateTotal())}</strong>.
              </p>
              
              <p style="color: #666; line-height: 1.6; margin-bottom: 25px;">
                Please review the updated invoice by clicking the button below:
              </p>
              
              <div style="text-align: center; margin: 30px 0;">
                <a href="${invoiceUrl}" style="display: inline-block; background-color: #FFB27D; color: black; padding: 12px 30px; text-decoration: none; border-radius: 8px; font-weight: 500; font-size: 16px;">
                  View Updated Invoice
                </a>
              </div>
              
              <p style="color: #666; line-height: 1.6; margin-bottom: 15px;">
                We apologize for any confusion the previous version may have caused. If you have any questions, 
                please don't hesitate to reach out.
              </p>
              
              <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee;">
                <p style="color: #999; font-size: 12px; margin: 0;">
                  Best regards,<br>
                  ${invoiceData.fromName || invoiceData.businessInfo?.businessName || 'Your Service Provider'}
                </p>
                <p style="color: #999; font-size: 11px; margin-top: 10px;">
                  Sent with <a href="https://klorah.app" style="color: #FFB27D; text-decoration: none;">Klorah</a>
                </p>
              </div>
            </div>
          `;

          await EmailService.sendEmail({
            user_id: currentUser.uid,
            to: invoiceData.email || invoiceData.clientEmail,
            subject: `Updated Invoice: ${invoiceData.project || 'Invoice'}`,
            body: emailBody,
            account_id: userAccounts[0].accountId
          });

          // Update sentDate since we've resent
          const db = getFirestore();
          const invoiceRef = doc(db, 'invoices', invoiceId);
          await updateDoc(invoiceRef, {
            sentDate: new Date(),
            updatedAt: new Date()
          });

          toast.success('Invoice updated and sent!');
        } catch (emailError) {
          console.error('Error sending email:', emailError);
          
          // Provide specific error messages
          if (emailError.message.includes('account')) {
            toast.error('Email account not found. Please reconnect your email in Settings.');
          } else if (emailError.message.includes('quota') || emailError.message.includes('limit')) {
            toast.error('Email quota exceeded. Please try again later.');
          } else {
            toast.success('Invoice updated! (Email failed to send - please check your connection)');
          }
        }
      } else {
        toast.success('Invoice updated successfully!');
      }

      navigate(`/preview-invoice/${invoiceId}`);
      
    } catch (error) {
      console.error('Error updating invoice:', error);
      toast.error('Failed to update invoice');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (!invoiceData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">Invoice not found</p>
        </div>
      </div>
    );
  }

  const total = calculateTotal();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white dark:bg-[#111111] rounded-xl shadow-sm border border-gray-200 dark:border-[#1A1A1A] p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Invoice</h1>
            <button
              onClick={() => navigate(`/preview-invoice/${invoiceId}`)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Warning if invoice was already sent */}
          {invoiceData.sentDate && (
            <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-amber-600 dark:text-amber-500 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">
                    This invoice was already sent to the client
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    Make sure to resend the invoice after making changes so the client receives the updated version.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Basic Info */}
        <div className="bg-white dark:bg-[#111111] rounded-xl shadow-sm border border-gray-200 dark:border-[#1A1A1A] p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Client Name
              </label>
              <input
                type="text"
                value={invoiceData.clientName || invoiceData.client || ''}
                onChange={(e) => handleChange('clientName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-brand focus:border-brand dark:bg-black dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Client Email
              </label>
              <input
                type="email"
                value={invoiceData.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-brand focus:border-brand dark:bg-black dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Project/Description
              </label>
              <input
                type="text"
                value={invoiceData.project || ''}
                onChange={(e) => handleChange('project', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-brand focus:border-brand dark:bg-black dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Due Date
              </label>
              <DatePicker
                selected={invoiceData.dueDate}
                onChange={(date) => handleChange('dueDate', date)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-brand focus:border-brand dark:bg-black dark:text-white"
                dateFormat="MM/dd/yyyy"
              />
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white dark:bg-[#111111] rounded-xl shadow-sm border border-gray-200 dark:border-[#1A1A1A] p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Line Items</h2>
          
          {invoiceData.lineItems && invoiceData.lineItems.length > 0 ? (
            <LineItemsManager
              lineItems={invoiceData.lineItems}
              setLineItems={(items) => handleChange('lineItems', items)}
              currency={invoiceData.currency || 'USD'}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Quantity
                </label>
                <input
                  type="number"
                  value={invoiceData.quantity || 1}
                  onChange={(e) => handleChange('quantity', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-brand focus:border-brand dark:bg-black dark:text-white"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rate
                </label>
                <input
                  type="number"
                  value={invoiceData.rate || invoiceData.amount || ''}
                  onChange={(e) => handleChange('rate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-brand focus:border-brand dark:bg-black dark:text-white"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subtotal
                </label>
                <div className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: invoiceData.currency || 'USD'
                  }).format((parseFloat(invoiceData.quantity || 1) * parseFloat(invoiceData.rate || invoiceData.amount || 0)))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tax */}
        <div className="bg-white dark:bg-[#111111] rounded-xl shadow-sm border border-gray-200 dark:border-[#1A1A1A] p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tax</h2>
          <TaxCalculator
            taxRate={invoiceData.taxRate || 0}
            taxType={invoiceData.taxType || 'percentage'}
            onChange={(tax) => {
              handleChange('taxRate', tax.rate);
              handleChange('taxType', tax.type);
            }}
          />
        </div>

        {/* Total */}
        <div className="bg-white dark:bg-[#111111] rounded-xl shadow-sm border border-gray-200 dark:border-[#1A1A1A] p-6 mb-6">
          <div className="flex justify-between items-center">
            <span className="text-xl font-semibold text-gray-900 dark:text-white">Total Amount</span>
            <span className="text-2xl font-bold text-brand">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: invoiceData.currency || 'USD'
              }).format(total)}
            </span>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white dark:bg-[#111111] rounded-xl shadow-sm border border-gray-200 dark:border-[#1A1A1A] p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Notes</h2>
          <textarea
            value={invoiceData.notes || ''}
            onChange={(e) => handleChange('notes', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-brand focus:border-brand dark:bg-black dark:text-white"
            rows="3"
            placeholder="Additional notes or payment instructions..."
          />
        </div>

        {/* Actions */}
        <div className="bg-white dark:bg-[#111111] rounded-xl shadow-sm border border-gray-200 dark:border-[#1A1A1A] p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => handleSave(false)}
              disabled={isSaving}
              className="flex-1 px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>

            {userAccounts.length > 0 && (
              <button
                onClick={() => handleSave(true)}
                disabled={isSaving}
                className="flex-1 px-6 py-3 bg-brand hover:bg-brand-hover text-black rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {isSaving ? 'Saving...' : 'Save & Resend'}
              </button>
            )}

            <button
              onClick={() => navigate(`/preview-invoice/${invoiceId}`)}
              className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
          </div>

          {userAccounts.length === 0 && (
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 text-center">
              Connect an email account in Settings to resend invoices after editing
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditInvoice;